import React, { useCallback } from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import type { InCartProduct } from '../../ShoppingCart.types';
import { InSummaryItem } from './InSummaryItem';
import { useTranslation } from '@shared/core';
import { StyledPriceLine, StyledDivider } from './SummaryItemList.styles';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useGiftWrapECardPrice } from '@shared/components/GiftWrap';
import { useShoppingCart } from '../../state';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { interpretShippingDecimalString } from '@apps/registry/common/utils/shippingAddress';
import { useRegistryShippingAndHandlingLineLabel } from '@apps/registry/common/hooks/useRegistryShippingAndHandlingLineLabel';

type SummaryItemListProps = Readonly<{
  inCartProductList: Array<InCartProduct>;
  inCartProductTotal: number;
  registryCurrencyCode?: string;
  hasECard?: boolean;
}>;

const PriceLine = ({ label, price, bold }: { label: string; price: string | number; bold?: boolean }) => (
  <StyledPriceLine>
    <TextV2
      typographyVariant={bold ? 'hed3' : 'label2'}
      color={bold ? 'mono14' : 'mono12'}
    >
      {label}
    </TextV2>
    <TextV2
      typographyVariant={bold ? 'hed3' : 'label2'}
      color={bold ? 'mono14' : 'mono12'}
    >
      {price}
    </TextV2>
  </StyledPriceLine>
);

export const SummaryItemList: React.FC<SummaryItemListProps> = ({ inCartProductList, inCartProductTotal, registryCurrencyCode, hasECard }) => {
  const { t } = useTranslation('sharedRegistry');
  const { subtotal, total, salesTax, delivery, freeDeliveryPriceText, pendingSalesTaxPriceText, eCard } = t('shoppingCart');
  const { formatCurrency } = useCurrencyFormatter();
  const isMobileOrTablet = useIsMobileOrTablet();
  const registryGiftWrapEcardPrice = useGiftWrapECardPrice();
  const { getSurchargeLineLabel } = useRegistryShippingAndHandlingLineLabel();
  const PRICE = `$${registryGiftWrapEcardPrice}`;
  const {
    state: { shippingFee, shippingFeeInMajorUnits, surchargeFee, additionalCostLines, additionalCostInMajorUnits, taxesTotalInMajorUnits },
  } = useShoppingCart();
  const shippingFeeInterpretation = interpretShippingDecimalString(shippingFee);

  const formatPrice = useCallback(
    (valueInMajorUnits: number) => {
      return registryCurrencyCode
        ? formatCurrency({
            priceFloatingPointDecimalString: `${valueInMajorUnits}`,
            priceCurrencyCode: DEFAULT_CURRENCY,
            formatForm: 'short',
            shouldCalculateCharmPrice: true,
          })
        : valueInMajorUnits;
    },
    [registryCurrencyCode, formatCurrency],
  );

  const additionalCostPriceLines = additionalCostLines.map((line, index) => (
    <PriceLine
      key={`costLine-${line.title}-${index}`}
      label={line.title}
      price={formatPrice((line.lineValue?.valueInMinorUnits ?? 0) / 100)}
    />
  ));

  const subtotalPrice = formatPrice(inCartProductTotal);

  const itemTotalInMajorUnits = inCartProductTotal + (hasECard ? registryGiftWrapEcardPrice : 0);
  const totalInMajorUnits = itemTotalInMajorUnits + shippingFeeInMajorUnits + (surchargeFee?.totalInMajorUnits ?? 0) + additionalCostInMajorUnits + (taxesTotalInMajorUnits ?? 0);
  const totalPrice = formatPrice(totalInMajorUnits);

  return (
    <>
      <Box flex={isMobileOrTablet ? '1' : undefined}>
        {inCartProductList.map(product => (
          <InSummaryItem
            key={product.registryItemId}
            inCartProduct={product}
          />
        ))}
      </Box>
      {!isMobileOrTablet && <StyledDivider />}
      <Box
        position={isMobileOrTablet ? 'sticky' : 'initial'}
        bottom="0"
        backgroundColor={isMobileOrTablet ? 'white' : undefined}
      >
        <Box>
          <PriceLine
            label={subtotal()}
            price={subtotalPrice}
          />
          {hasECard && (
            <PriceLine
              label={eCard()}
              price={PRICE}
            />
          )}
          <PriceLine
            label={delivery()}
            price={shippingFeeInterpretation.isFreeShipping ? freeDeliveryPriceText() : shippingFeeInterpretation.floatingPointCurrencyString}
          />
          {surchargeFee === undefined && (
            <PriceLine
              label={getSurchargeLineLabel()}
              price={pendingSalesTaxPriceText()}
            />
          )}
          {surchargeFee && surchargeFee.totalInMajorUnits > 0 && (
            <PriceLine
              label={getSurchargeLineLabel(surchargeFee.itemsCount)}
              price={formatPrice(surchargeFee.totalInMajorUnits)}
            />
          )}
          {additionalCostPriceLines}
          <PriceLine
            label={salesTax()}
            price={taxesTotalInMajorUnits === undefined ? pendingSalesTaxPriceText() : formatPrice(taxesTotalInMajorUnits)}
          />
        </Box>
        <Box
          paddingX={5}
          paddingY={1}
        >
          <StyledDivider margin="0 !important" />
        </Box>
      </Box>
      {!isMobileOrTablet && (
        <PriceLine
          label={total()}
          price={totalPrice}
          bold
        />
      )}
    </>
  );
};
